import { useState, useEffect, useRef } from "react";
import toast from "react-hot-toast";
import { Snackbar, FormControl, Select, MenuItem, Box, Paper, Button, IconButton, Menu } from "@mui/material";
import { supabase } from "../../../../supabase";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import { useMainContext } from "../../../../context/useMainContext";
import DeleteConfirmDialog from "./DeleteConfirmDialog";

export default function DeleteSelectedRows() {
  const [open, setOpen] = useState(false);
  const [openDotsMenu, setOpenDotsMenu] = useState(null);
  const openDots = Boolean(openDotsMenu);
  const [prevSelectedLength, setPrevSelectedLength] = useState(0);
  const deleteDialogRef = useRef(null);
  const { user, selected, setSelected, availableUsers, isGuest, setRows } = useMainContext();

  useEffect(() => {
    const currentLength = selected.length;
    if (currentLength > prevSelectedLength && currentLength > 0) {
      setOpen(true);
    } else if (currentLength === 0) {
      setOpen(false);
    }
    setPrevSelectedLength(currentLength);
  }, [selected.length, prevSelectedLength]);

  const handleDeleteClick = () => {
    deleteDialogRef.current?.handleDeleteClick();
  };

  const handleBulkAssignment = async (e) => {
    const selectedValue = e.target.value;
    if (!selectedValue) return;
    const assignedMember = selectedValue === "unassigned" ? null : selectedValue;

    try {
      if (selected.length === 0) {
        toast.error("No profiles selected.");
        return;
      }

      const selectedIds = selected.filter((item) => !item.isDetailRow).map((item) => item.id);

      if (selectedIds.length === 0) {
        toast.error("No profiles selected for assignment.");
        return;
      }

      const { error } = await supabase
        .from("influencers")
        .update({ assigned_member: assignedMember })
        .in("id", selectedIds);

      if (error) {
        console.error("❌ Database update failed:", error);
        toast.error("Failed to assign member to selected profiles.");
        return;
      }

      const memberDisplayName = assignedMember || "Unassigned";
      toast.success(`${selectedIds.length} profiles assigned to ${memberDisplayName}`);
      setRows((prevRows) =>
        prevRows.map((row) => (selectedIds.includes(row.id) ? { ...row, assigned_member: assignedMember } : row))
      );
      setSelected((prevSelected) =>
        prevSelected.map((item) =>
          selectedIds.includes(item.id) ? { ...item, assigned_member: assignedMember } : item
        )
      );
      setOpen(false);
    } catch (error) {
      console.error("💥 Bulk assignment failed:", error);
      toast.error("Failed to assign member to selected profiles.");
    }
  };

  return (
    <>
      <Snackbar
        open={open}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        sx={{
          zIndex: 1,
        }}
      >
        <Paper
          elevation={3}
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
            p: 2,
            pt: 1,
            minWidth: 400,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {selected.length} Selected rows
              <Button onClick={() => setSelected([])} variant="outlined">
                Deselect
              </Button>
            </Box>
            <IconButton onClick={() => setOpen(false)}>
              <CloseOutlinedIcon />
            </IconButton>
          </Box>

          <Box sx={{ display: "flex", gap: 1 }}>
            <FormControl size="small" fullWidth>
              <Select
                value=""
                onChange={handleBulkAssignment}
                displayEmpty
                disabled={isGuest}
                IconComponent={ExpandMoreIcon}
              >
                <MenuItem value="" disabled>
                  <em>Select Member</em>
                </MenuItem>
                <MenuItem value="unassigned">Unassigned</MenuItem>
                {availableUsers.map((user) => (
                  <MenuItem key={user.id} value={user.name}>
                    {user.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              variant="outlined"
              onClick={(e) => setOpenDotsMenu(e.currentTarget)}
              disableElevation
              aria-haspopup="true"
              aria-expanded={openDots ? "true" : undefined}
              disabled={user?.role !== "admin"}
              sx={{ px: 1 }}
            >
              <MoreHorizIcon />
            </Button>

            <Menu
              anchorEl={openDotsMenu}
              open={openDots}
              onClose={() => setOpenDotsMenu(null)}
              anchorOrigin={{
                vertical: "top",
                horizontal: "left",
              }}
              transformOrigin={{
                vertical: "bottom",
                horizontal: "left",
              }}
            >
              <MenuItem onClick={handleDeleteClick} sx={{ color: "error.main" }}>
                Delete Selected
              </MenuItem>
            </Menu>
          </Box>
        </Paper>
      </Snackbar>

      <DeleteConfirmDialog
        ref={deleteDialogRef}
        {...{
          setOpenDotsMenu,
          setOpen,
        }}
      />
    </>
  );
}
